import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { CANTONS, cantonByOption } from '../../../../util/regions';
import { OutsideClickHandler } from '../../../../components';

import css from './SearchRegionSegment.module.css';

const KEY_CODE_ESCAPE = 27;

/**
 * What Wo reads once cantons are chosen: the one name, or the first plus a count. null when none
 * is chosen, so the caller shows its placeholder. The phone search bar reads the same words.
 *
 * @param {Array<string>} regions selected canton option keys
 * @param {Object} intl
 * @returns {string|null}
 */
export const regionSummary = (regions = [], intl) => {
  const selected = regions.map(cantonByOption).filter(Boolean);
  return selected.length === 0
    ? null
    : selected.length === 1
    ? selected[0].label
    : intl.formatMessage(
        { id: 'TopbarSearchForm.regionSummaryMore' },
        { region: selected[0].label, count: selected.length - 1 }
      );
};

/**
 * The "Wo" half of the topbar search: the cantons the wedding could be in.
 *
 * Multi-select. Ticking only updates the pill's summary; nothing searches until the pill's own
 * submit is pressed. That is what lets a couple set Wann and Wo together before searching, and
 * it is why there is no apply button in here — an apply that only applied half the pill would
 * be the confusing one.
 *
 * The list is the same CANTONS table the sidebar filter and the /region/ pages read, so there
 * is one canton vocabulary in the codebase rather than a header copy of it.
 *
 * What more than one canton MEANS is the `Region` field's search mode, not this component's
 * choice — see regionParamValue in util/regions. Under the mode currently configured
 * ('has_all') two cantons asks for suppliers covering both.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {Array<string>} props.regions selected canton option keys
 * @param {Function} props.onChange called with the next array of option keys
 * @returns {JSX.Element} region segment
 */
const SearchRegionSegment = props => {
  const { className, regions = [], onChange } = props;
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);

  const label = regionSummary(regions, intl);

  // An aria-label replaces the visible text as the accessible name, so a bare "Wo" would hide
  // the chosen cantons from a screen reader. Nothing to add when nothing is chosen: the two
  // lines already read "Wo" and the placeholder.
  const ariaLabel = label
    ? intl.formatMessage({ id: 'TopbarSearchForm.regionLabelSelected' }, { region: label })
    : undefined;

  const close = () => setIsOpen(false);

  const onEscape = e => {
    if (e.keyCode === KEY_CODE_ESCAPE) {
      close();
    }
  };

  const toggle = option =>
    onChange(regions.includes(option) ? regions.filter(o => o !== option) : [...regions, option]);

  return (
    <OutsideClickHandler className={classNames(css.root, className)} onOutsideClick={close}>
      <button
        type="button"
        className={css.trigger}
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => setIsOpen(open => !open)}
        onKeyDown={onEscape}
      >
        <span className={css.triggerLabel}>
          {intl.formatMessage({ id: 'TopbarSearchForm.regionLabel' })}
        </span>
        <span className={classNames(css.triggerValue, { [css.triggerValueSet]: !!label })}>
          {label || intl.formatMessage({ id: 'TopbarSearchForm.regionPlaceholder' })}
        </span>
      </button>

      {isOpen ? (
        <div className={css.popover} onKeyDown={onEscape}>
          <fieldset
            className={css.fieldset}
            aria-label={intl.formatMessage({ id: 'TopbarSearchForm.regionPopoverLabel' })}
          >
            <ul className={css.list}>
              {CANTONS.map(canton => {
                const id = `topbar-region-${canton.option}`;
                return (
                  <li key={canton.option}>
                    <input
                      type="checkbox"
                      id={id}
                      className={css.checkbox}
                      checked={regions.includes(canton.option)}
                      onChange={() => toggle(canton.option)}
                    />
                    <label className={css.option} htmlFor={id}>
                      {canton.label}
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>

          <button type="button" className={css.clear} onClick={() => onChange([])}>
            <FormattedMessage id="TopbarSearchForm.regionClear" />
          </button>
        </div>
      ) : null}
    </OutsideClickHandler>
  );
};

export default SearchRegionSegment;
